import { useEffect, useMemo, useRef, useState, type ChangeEvent, type KeyboardEvent, type PointerEvent as ReactPointerEvent } from 'react';
import {
  Color, RECORD_PICTURE_MAX_PX, RECORD_PICTURE_ZOOM_MAX, RECORD_PICTURE_ZOOM_MIN, Size, byteText, centredPlacement, clampPlacement, clampZoom,
  cropFor, cropSide, isRecordPictureMime, t, type RecordPicturePlacement, type RecordPictureSource,
} from '@bms2/shared';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { Button } from '../../components/controls/Button';
import { Modal } from '../../components/modals/Modal';
import { PAGES_PATH, del, upload } from './api';
import { scaleImageInBrowser } from './ItemModals';
import styles from './RecordPictureModal.module.css';

/**
 * The person's one photograph (piece 13, decision 0616), on the Item image's own
 * path (decision 0269). 🔨 The browser scales the picked picture into the
 * 512-pixel box before the upload, so a phone photo never crosses the wire at
 * full size; the server scales it again, which is the guard.
 *
 * 🔨 Relative to the API root, never prefixed with /api.
 */
export function personPhotoPath(personId: string): string {
  return `${PAGES_PATH}/people/${personId}/photo`;
}

/**
 * The company's one logo (T170), on the person photograph's own path and with the
 * same bound. 🔨 It is the Company's own picture and never the Organization's: our
 * own record reads the organisation logo of decision 0575, which Settings owns.
 */
export function companyLogoPath(companyId: string): string {
  return `${PAGES_PATH}/companies/${companyId}/logo`;
}

/**
 * A Location's one photograph (decision 0667), on the company logo's own path and
 * with the same bound: a picture of the building, set through the same badge.
 */
export function locationPhotoPath(locationId: string): string {
  return `${PAGES_PATH}/locations/${locationId}/photo`;
}

/** Which record's picture is being set, which is the only thing that differs between the three. */
export type RecordPictureKind = 'person' | 'company' | 'location';

/**
 * 🔨 The wording is a map and not three components: the flow is the same pick,
 * scale, upload and remove for all three, and a second modal would be one screen
 * kept in two places.
 */
const WORDING = {
  person: { title: 'person.photo.title', hint: 'person.photo.hint', pick: 'person.photo.pick', save: 'person.photo.save', remove: 'person.photo.remove', none: 'person.photo.none', pickFirst: 'person.photo.pickFirst', alt: 'person.photo.alt', modal: 'person-photo', path: personPhotoPath },
  company: { title: 'company.logo.title', hint: 'company.logo.hint', pick: 'company.logo.pick', save: 'company.logo.save', remove: 'company.logo.remove', none: 'company.logo.none', pickFirst: 'company.logo.pickFirst', alt: 'company.logo.alt', modal: 'company-logo', path: companyLogoPath },
  location: { title: 'location.photo.title', hint: 'location.photo.hint', pick: 'location.photo.pick', save: 'location.photo.save', remove: 'location.photo.remove', none: 'location.photo.none', pickFirst: 'location.photo.pickFirst', alt: 'location.photo.alt', modal: 'location-photo', path: locationPhotoPath },
} as const;

export interface RecordPictureModalProps {
  open: boolean;
  kind: RecordPictureKind;
  recordId: string;
  /** The picture already set, which is what offers Remove. */
  pictureFileId: string | null;
  apiBaseUrl?: string;
  onCancel: () => void;
  onSaved: () => void;
}

export function RecordPictureModal({ open, kind, recordId, pictureFileId, apiBaseUrl = '', onCancel, onSaved }: RecordPictureModalProps) {
  const words = WORDING[kind];
  const [picked, setPicked] = useState<File | null>(null);
  const [scaled, setScaled] = useState<{ blob: Blob; width: number; height: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  /**
   * 🔒 Where the picture sits behind the round frame, and how far in it is zoomed
   * (decision 0624). The window holds the centre in the picture's own pixels, so
   * zooming keeps whatever is under the middle of the frame under it.
   */
  const [sourceSize, setSourceSize] = useState<RecordPictureSource | null>(null);
  const [placement, setPlacement] = useState<RecordPicturePlacement | null>(null);
  const frameRef = useRef<HTMLDivElement | null>(null);
  const drag = useRef<{ pointerId: number; x: number; y: number } | null>(null);

  /**
   * The size of the picture the crop is measured against: the scaled blob's where
   * the browser could scale it, and the drawn picture's own where it could not.
   * 🔨 The placement opens centred at 1×, which is the circle the window drew
   * before there was a crop step.
   */
  const sizeFound = (found: RecordPictureSource) => {
    if (found.width <= 0 || found.height <= 0) return;
    setSourceSize(found);
    setPlacement((was) => was ?? centredPlacement(found));
  };

  // The scaling is asynchronous, so the effect only ever writes from its callback.
  useEffect(() => {
    if (!picked) return undefined;
    let cancelled = false;
    void scaleImageInBrowser(picked, RECORD_PICTURE_MAX_PX).then((result) => {
      if (cancelled) return;
      setScaled(result);
      // The scaled blob is what travels, so the crop is measured in its pixels and not the original's.
      if (result) sizeFound({ width: result.width, height: result.height });
    });
    return () => {
      cancelled = true;
    };
  }, [picked]);

  const source = scaled?.blob ?? picked;
  const preview = useMemo(() => (source && typeof URL.createObjectURL === 'function' ? URL.createObjectURL(source) : null), [source]);
  useEffect(() => () => {
    if (preview) URL.revokeObjectURL(preview);
  }, [preview]);

  const pick = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setError(null);
    setScaled(null);
    setSourceSize(null);
    setPlacement(null);
    if (file && !isRecordPictureMime(file.type)) {
      setPicked(null);
      setError(t('record.picture.notImage'));
      return;
    }
    setPicked(file);
  };

  /** What the server cuts: the square the frame is showing, in the sent picture's own pixels. */
  const crop = sourceSize && placement ? cropFor(sourceSize, placement) : null;

  /** One step of a keyboard nudge, and of a drag: a twentieth of what the frame is showing. */
  const nudge = (dx: number, dy: number) => {
    if (!sourceSize || !placement) return;
    setPlacement(clampPlacement(sourceSize, { ...placement, centreX: placement.centreX + dx, centreY: placement.centreY + dy }));
  };

  const zoomTo = (zoom: number) => {
    if (!sourceSize || !placement) return;
    // 🔨 Clamped after the zoom, because zooming out can leave the centre too near an edge to fill the frame.
    setPlacement(clampPlacement(sourceSize, { ...placement, zoom: clampZoom(zoom) }));
  };

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!placement) return;
    drag.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY };
    // Capture keeps the drag alive past the frame's own edge; a browser without it still drags inside the frame.
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const held = drag.current;
    if (!held || held.pointerId !== event.pointerId || !sourceSize || !placement) return;
    const frame = frameRef.current?.clientWidth ?? 0;
    if (frame <= 0) return;
    // A pixel of the frame is this many pixels of the picture, so a drag moves the crop by what the eye sees.
    const perPixel = cropSide(sourceSize, placement.zoom) / frame;
    drag.current = { pointerId: held.pointerId, x: event.clientX, y: event.clientY };
    nudge(-(event.clientX - held.x) * perPixel, -(event.clientY - held.y) * perPixel);
  };

  const onPointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (drag.current?.pointerId === event.pointerId) drag.current = null;
  };

  /** 🔒 Arrow keys place the picture and plus and minus zoom it, so the step is not a drag alone (decision 0624). */
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!sourceSize || !placement) return;
    const step = cropSide(sourceSize, placement.zoom) / 20;
    const handled = () => {
      event.preventDefault();
      event.stopPropagation();
    };
    switch (event.key) {
      case 'ArrowLeft': return handled(), nudge(-step, 0);
      case 'ArrowRight': return handled(), nudge(step, 0);
      case 'ArrowUp': return handled(), nudge(0, -step);
      case 'ArrowDown': return handled(), nudge(0, step);
      case '+':
      case '=': return handled(), zoomTo(placement.zoom + 0.25);
      case '-':
      case '_': return handled(), zoomTo(placement.zoom - 0.25);
      default: return undefined;
    }
  };

  const save = async () => {
    setError(null);
    if (!picked) return setError(t(words.pickFirst));
    setSaving(true);
    const form = new FormData();
    form.append('file', scaled?.blob ?? picked, picked.name);
    // 🔒 The crop travels as request fields and never as columns: what is stored is the result (decision 0624).
    if (crop) {
      form.append('cropX', String(crop.x));
      form.append('cropY', String(crop.y));
      form.append('cropWidth', String(crop.width));
      form.append('cropHeight', String(crop.height));
    }
    const result = await upload(words.path(recordId), form, apiBaseUrl);
    setSaving(false);
    if (!result.ok) return setError(result.message);
    onSaved();
    onCancel();
  };

  const remove = async () => {
    setError(null);
    setSaving(true);
    const result = await del(words.path(recordId), apiBaseUrl);
    setSaving(false);
    if (!result.ok) return setError(result.message);
    onSaved();
    onCancel();
  };

  const storedText = scaled
    ? `${t('record.picture.storedAt')} ${scaled.width} × ${scaled.height} px · ${byteText(scaled.blob.size)}`
    : picked
      ? byteText(picked.size)
      : null;

  return (
    <Modal
      open={open}
      title={t(words.title)}
      subtitle={t(words.hint)}
      onCancel={onCancel}
      dataModal={words.modal}
      actions={[
        { kind: 'primary', label: t(words.save), onAct: () => void save(), disabled: saving || !picked },
        { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel },
      ]}
    >
      {error ? <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} /> : null}
      <label className={styles.field}>
        <span className={styles.fieldLabel}>{t(words.pick)}</span>
        <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={pick} data-record-picture-input="true" />
      </label>
      {/*
        🔒 The drag-to-place and zoom step (decision 0624): the picture sits inside a
        round frame the banner's own shape, drawn large enough to judge a face by,
        and what the frame shows is exactly what the server cuts.
      */}
      {preview ? (
        <div className={styles.preview} data-record-picture-preview="true">
          <div
            ref={frameRef}
            className={styles.cropFrame}
            role="group"
            tabIndex={0}
            aria-label={t('record.picture.frame')}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onKeyDown={onKeyDown}
            data-record-picture-frame="true"
            data-record-picture-crop={crop ? `${crop.x},${crop.y},${crop.width},${crop.height}` : undefined}
          >
            <img
              src={preview}
              alt={t(words.alt)}
              draggable={false}
              onLoad={(event) => sizeFound({ width: event.currentTarget.naturalWidth, height: event.currentTarget.naturalHeight })}
              {...(sourceSize && crop
                ? {
                    // The picture is drawn at the scale the crop stands for, so the frame is a window on it.
                    style: {
                      width: `${(sourceSize.width / crop.width) * 100}%`,
                      height: `${(sourceSize.height / crop.height) * 100}%`,
                      left: `${(-crop.x / crop.width) * 100}%`,
                      top: `${(-crop.y / crop.height) * 100}%`,
                    },
                  }
                : {})}
            />
          </div>
          {placement ? (
            <label className={styles.zoom}>
              <span className={styles.fieldLabel}>{t('record.picture.zoom')}</span>
              <input
                type="range"
                min={RECORD_PICTURE_ZOOM_MIN}
                max={RECORD_PICTURE_ZOOM_MAX}
                step={0.05}
                value={placement.zoom}
                onChange={(event) => zoomTo(Number(event.target.value))}
                aria-label={t('record.picture.zoom')}
                data-record-picture-zoom="true"
              />
            </label>
          ) : null}
        </div>
      ) : null}
      {preview ? <p className={styles.hint} data-record-picture-place-hint="true">{t('record.picture.placeHint')}</p> : null}
      {storedText ? <p className={styles.hint} data-record-picture-result="true">{storedText}</p> : null}
      {/* 🔨 Remove is offered only where a picture is set, and it trashes the file rather than destroying it. */}
      {pictureFileId ? (
        <p className={styles.hint}>
          <Button size={Size.Small} color={Color.Inactive} text={[t(words.remove)]} actions={[() => void remove()]} disabled={saving} />
        </p>
      ) : (
        <p className={styles.hint}>{t(words.none)}</p>
      )}
    </Modal>
  );
}
